"use client";

import { createContext, useContext, useEffect } from "react";

import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import type { Profile } from "../../models";
import type { PostHogUserIdentity } from "./posthog-common";
import {
  type PostHogFlags,
  PostHogFlagsProvider,
} from "./posthog-flags-provider";

export const identifyUser = (profile: Profile) => {
  posthog.identify(profile.user.auth.provider_id, {
    email: profile.user.email,
    name: profile.user.name,
    internal_id: profile.user.id,
  });
};

interface PHOrgMetadata {
  name: string; // e.g. "Groq Test Org"
  plan: string; // e.g. developer-self-serve-1
}

export const identifyOrg = (orgId: string, metadata: PHOrgMetadata) => {
  posthog.group("org_id", orgId, {
    name: metadata.name,
    plan: metadata.plan,
  });
};

interface PostHogClientProviderProps {
  children: React.ReactNode;
  flags: PostHogFlags;
  userIdentity: PostHogUserIdentity;
}

const PostHogUserIdentityContext = createContext<PostHogUserIdentity | null>(
  null,
);

export const usePostHogUserIdentity = () => {
  return useContext(PostHogUserIdentityContext);
};

export function PostHogClientProvider({
  children,
  flags,
  userIdentity,
}: PostHogClientProviderProps) {
  useEffect(() => {
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
      api_host: "/a",
      ui_host: "https://us.posthog.com",
      person_profiles: "identified_only", // or 'always' to create profiles for anonymous users as well
      capture_pageview: "history_change",
      bootstrap: {
        featureFlags: flags,
        distinctID: userIdentity.id,
        isIdentifiedID: userIdentity.isIdentified,
      },
    });
  }, []);

  return (
    <PostHogUserIdentityContext.Provider value={userIdentity}>
      <PHProvider client={posthog}>
        <PostHogFlagsProvider flags={flags}>{children}</PostHogFlagsProvider>
      </PHProvider>
    </PostHogUserIdentityContext.Provider>
  );
}
